import React, { useEffect, useMemo, useState } from 'react';
import {
  AgricultureOutlined,
  CheckCircleRounded,
  FlagOutlined,
  KeyboardArrowRightRounded,
  MoreHorizRounded,
  PlaceOutlined,
  PaymentsOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import signupFlowSteps from './signupFlowConfig';
import { step3Schema } from './validationSchemas';
import SignupProgress from './signupProgress';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';
import {
  getInitiativesByCountry,
  getInitiativesOfferedByIntegrators,
  getIntegratorsByCountry,
} from './login.service';
import { useSignupMetaData } from '../hooks/useSignupMetaData';
import { SCORE_CUSTOMER_PERSONA, isScoreCustomerPersona } from './scoreCustomerSignup';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const ecosystemTypeOptions = [
  { id: 'area', label: 'Area', Icon: PlaceOutlined },
  { id: 'initiative', label: 'Initiative', Icon: FlagOutlined },
  { id: 'crop', label: 'Crop', Icon: AgricultureOutlined },
  { id: 'finance', label: 'Finance', Icon: PaymentsOutlined },
  { id: 'other', label: 'Other', Icon: MoreHorizRounded },
];

const typeKeywordMap = {
  area: ['area', 'region', 'regional', 'zone', 'territory', 'location'],
  initiative: ['initiative', 'program', 'programme', 'project', 'mission'],
  crop: ['crop', 'agri', 'agriculture', 'farm', 'farming', 'seed'],
  finance: ['finance', 'financial', 'credit', 'loan', 'insurance', 'payment'],
  other: ['other', 'general', 'community', 'misc', 'support'],
};


const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const toArray = (value) => (Array.isArray(value) ? value : []);

const resolveTypeId = (...candidates) => {
  const normalizedCandidates = candidates
    .map((candidate) => normalizeText(candidate))
    .filter(Boolean);

  for (const candidate of normalizedCandidates) {
    const directMatch = ecosystemTypeOptions.find((option) => option.id === candidate);
    if (directMatch) {
      return directMatch.id;
    }

    const labelMatch = ecosystemTypeOptions.find(
      (option) => normalizeText(option.label) === candidate
    );
    if (labelMatch) {
      return labelMatch.id;
    }

    for (const [typeId, keywords] of Object.entries(typeKeywordMap)) {
      const isMatch = keywords.some(
        (keyword) => candidate.includes(keyword) || keyword.includes(candidate)
      );

      if (isMatch) {
        return typeId;
      }
    }
  }

  return null;
};

const normalizeInitiativeEntry = (entry, inheritedType) => {
  const label = getFirstDefinedValue(
    entry?.name,
    entry?.initiativeName,
    entry?.title,
    entry?.label,
    entry?.ecosystemName
  );

  if (!label) {
    return null;
  }

  const typeId =
    resolveTypeId(
      entry?.type,
      entry?.category,
      entry?.initiativeType,
      entry?.ecosystemType,
      entry?.ecosystemCategory,
      entry?.preferredType,
      inheritedType,
      label
    ) || 'initiative';

  return {
    id: getFirstDefinedValue(entry?.id, entry?.initiativeId, entry?.value, label),
    label,
    description: getFirstDefinedValue(
      entry?.description,
      entry?.summary,
      entry?.shortDescription,
      entry?.details
    ),
    typeId,
  };
};

const normalizeInitiativeGroups = (rawEcosystemData) => {
  const groups = ecosystemTypeOptions.reduce(
    (accumulator, option) => ({ ...accumulator, [option.id]: [] }),
    {}
  );

  rawEcosystemData.forEach((entry) => {
    const inheritedType = getFirstDefinedValue(
      entry?.name,
      entry?.type,
      entry?.category,
      entry?.ecosystemType
    );
    const nestedItems = getFirstDefinedValue(
      entry?.initiatives,
      entry?.items,
      entry?.children,
      entry?.options,
      entry?.initiativeList
    );

    if (Array.isArray(nestedItems) && nestedItems.length > 0) {
      nestedItems.forEach((nestedEntry) => {
        const normalizedEntry = normalizeInitiativeEntry(nestedEntry, inheritedType);
        if (normalizedEntry) {
          groups[normalizedEntry.typeId].push(normalizedEntry);
        }
      });
      return;
    }

    const normalizedEntry = normalizeInitiativeEntry(entry, inheritedType);
    if (normalizedEntry) {
      groups[normalizedEntry.typeId].push(normalizedEntry);
    }
  });

  return ecosystemTypeOptions.reduce((accumulator, option) => {
    const dedupedItems = groups[option.id].filter(
      (item, index, items) =>
        index === items.findIndex((candidate) => String(candidate.id) === String(item.id))
    );

    return {
      ...accumulator,
      [option.id]: dedupedItems,
    };
  }, {});
};

export default function SignupStep3({
  handleContinue,
  handleBack,
  handleChange,
  formData,
}) {
  const [initiatives, setInitiatives] = useState([]);
  const [initiativesLoaded, setInitiativesLoaded] = useState(false);
  const [integrators, setIntegrators] = useState([]);
  const [integratorsLoaded, setIntegratorsLoaded] = useState(false);
  const { userTypes } = useSignupMetaData(formData.administrativeRegion);
  const isDare = useMemo(
    () =>
      (userTypes || []).find(
        (userType) => String(userType.id) === String(formData.userTypeId)
      )?.typeName === 'DARE',
    [userTypes, formData.userTypeId]
  );
  // An eK Score Customer joins exactly one eK Score integrator; the backend lists only those for it.
  const isScoreCustomer = isScoreCustomerPersona(formData.personaName);
  const [selectedEcosystems, setSelectedEcosystems] = useState(
    Array.isArray(formData.selectedEcosystems) ? formData.selectedEcosystems : []
  );
  const [selectedIntegrators, setSelectedIntegrators] = useState(
    Array.isArray(formData.selectedIntegrators) ? formData.selectedIntegrators : []
  );
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // A DARE sees only the Initiatives its chosen area-based integrator's area joined (approval chain).
  const integratorKey = Array.isArray(formData.selectedIntegrators)
    ? formData.selectedIntegrators.map(String).join(',')
    : '';

  useEffect(() => {
    const loadInitiatives = async () => {
      setInitiativesLoaded(false);

      if (!formData.administrativeRegion || (isDare && !integratorKey)) {
        setInitiatives([]);
        setInitiativesLoaded(true);
        return;
      }

      try {
        const response = isDare
          ? await getInitiativesOfferedByIntegrators(formData.administrativeRegion, integratorKey.split(','))
          : await getInitiativesByCountry(formData.administrativeRegion);
        if (response.statusCode === 200 && Array.isArray(response.data)) {
          setInitiatives(response.data);
        } else {
          setInitiatives([]);
        }
      } catch (error) {
        setInitiatives([]);
      } finally {
        setInitiativesLoaded(true);
      }
    };

    loadInitiatives();
  }, [formData.administrativeRegion, isDare, integratorKey]);

  useEffect(() => {
    const loadIntegrators = async () => {
      setIntegratorsLoaded(false);
      if (!formData.administrativeRegion) {
        setIntegrators([]);
        return;
      }

      try {
        const response = await getIntegratorsByCountry(
          formData.administrativeRegion,
          isScoreCustomer ? SCORE_CUSTOMER_PERSONA : undefined
        );
        if (response.statusCode === 200 && Array.isArray(response.data)) {
          setIntegrators(response.data);
          setIntegratorsLoaded(true);
        } else {
          setIntegrators([]);
        }
      } catch (error) {
        setIntegrators([]);
      }
    };

    loadIntegrators();
  }, [formData.administrativeRegion, isScoreCustomer]);

  useEffect(() => {
    if (Array.isArray(formData.selectedEcosystems)) {
      setSelectedEcosystems(formData.selectedEcosystems);
    }
  }, [formData.selectedEcosystems]);

  useEffect(() => {
    if (Array.isArray(formData.selectedIntegrators)) {
      setSelectedIntegrators(formData.selectedIntegrators);
    }
  }, [formData.selectedIntegrators]);

  const selectedTypes = useMemo(
    () =>
      Array.isArray(formData.preferredEcosystemTypes)
        ? formData.preferredEcosystemTypes
        : [],
    [formData.preferredEcosystemTypes]
  );
  const selectedInitiatives = useMemo(
    () =>
      Array.isArray(formData.initiativeEcosystemToJoin)
        ? formData.initiativeEcosystemToJoin
        : [],
    [formData.initiativeEcosystemToJoin]
  );

  const initiativeGroups = useMemo(() => {
    const mappedInitiatives = initiatives.map((initiative) => ({
      id: initiative.id,
      name: initiative.name,
      description: initiative.description,
      ecosystemType: initiative.type?.name || 'initiative',
    }));

    const normalizedGroups = normalizeInitiativeGroups(mappedInitiatives);

    return ecosystemTypeOptions.reduce((accumulator, option) => {
      const realItems = normalizedGroups[option.id] || [];

      return {
        ...accumulator,
        [option.id]: realItems,
      };
    }, {});
  }, [initiatives]);

  const availableInitiatives = useMemo(
    () =>
      selectedTypes.flatMap((typeId) => {
        const initiatives = initiativeGroups[typeId] || [];
        return initiatives.map((initiative) => ({
          ...initiative,
          typeLabel:
            ecosystemTypeOptions.find((option) => option.id === typeId)?.label || '',
        }));
      }),
    [initiativeGroups, selectedTypes]
  );

  // EK-02: only a DARE, and only when its country has no integrator at all, may skip this.
  const integratorsOptional = isDare && integratorsLoaded && integrators.length === 0;
  const schemaContext = { context: { integratorsOptional, isDare } };

  const filteredIntegrators = useMemo(
    () => {
      if (selectedTypes.length === 0) return [];
      const selectedKeywords = selectedTypes.flatMap((typeId) => typeKeywordMap[typeId] || []);
      return integrators.filter((integrator) => {
        const normalizedType = normalizeText(integrator.integratorType);
        return selectedKeywords.some((keyword) => normalizedType.includes(keyword));
      });
    },
    [integrators, selectedTypes]
  );

  useEffect(() => {
    if (!initiativesLoaded || !selectedInitiatives.length) {
      return;
    }

    const availableIds = new Set(
      initiatives.map((initiative) => String(initiative.id))
    );
    const filteredSelections = selectedInitiatives.filter((initiativeId) =>
      availableIds.has(String(initiativeId))
    );

    if (filteredSelections.length !== selectedInitiatives.length) {
      handleChange({
        target: {
          name: 'initiativeEcosystemToJoin',
          value: filteredSelections,
        },
      });
    }
  }, [initiativesLoaded, initiatives, handleChange, selectedInitiatives]);

  const renderFieldLabel = (label, required = false) => (
    <Typography variant="subtitle2" sx={fieldLabelStyles}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  const setFieldValue = (name, value) => {
    handleChange({
      target: {
        name,
        value,
      },
    });
  };

  const validateField = async (name, value, overrides = {}) => {
    try {
      await step3Schema.validateAt(name, {
        ...formData,
        ...overrides,
        [name]: value,
      }, schemaContext);
      setErrors((currentErrors) => {
        if (!currentErrors[name]) {
          return currentErrors;
        }

        const nextErrors = { ...currentErrors };
        delete nextErrors[name];
        return nextErrors;
      });
    } catch (error) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [name]: error.message,
      }));
    }
  };

  const validateStep = async () => {
    try {
      await step3Schema.validate(formData, { abortEarly: false, ...schemaContext });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const handleTypeToggle = async (typeId) => {
    const nextTypes = selectedTypes.includes(typeId)
      ? selectedTypes.filter((selectedType) => selectedType !== typeId)
      : [...selectedTypes, typeId];

    const nextInitiativeIds = selectedInitiatives.filter((initiativeId) =>
      nextTypes.some((selectedType) =>
        (initiativeGroups[selectedType] || []).some(
          (initiative) => String(initiative.id) === String(initiativeId)
        )
      )
    );

    setTouched((currentTouched) => ({
      ...currentTouched,
      preferredEcosystemTypes: true,
      initiativeEcosystemToJoin: true,
    }));

    setFieldValue('preferredEcosystemTypes', nextTypes);
    setFieldValue('initiativeEcosystemToJoin', nextInitiativeIds);
    await validateField('preferredEcosystemTypes', nextTypes, {
      initiativeEcosystemToJoin: nextInitiativeIds,
    });

    if (nextInitiativeIds.length > 0) {
      await validateField('initiativeEcosystemToJoin', nextInitiativeIds, {
        preferredEcosystemTypes: nextTypes,
      });
    }
  };

  const handleEcosystemToggle = async (ecosystemId) => {
    const nextEcosystems = selectedEcosystems.includes(ecosystemId)
      ? selectedEcosystems.filter((selectedId) => selectedId !== ecosystemId)
      : [...selectedEcosystems, ecosystemId];

    setSelectedEcosystems(nextEcosystems);
    setTouched((currentTouched) => ({
      ...currentTouched,
      selectedEcosystems: true,
    }));

    setFieldValue('selectedEcosystems', nextEcosystems);
    await validateField('selectedEcosystems', nextEcosystems);
  };

  // An eK Score Customer keeps one integrator: drop picks that are no longer offered (e.g. made
  // before the persona changed) and anything beyond the first.
  useEffect(() => {
    if (!isScoreCustomer || !integratorsLoaded || !Array.isArray(formData.selectedIntegrators)) {
      return;
    }
    const offered = new Set(integrators.map((integrator) => String(integrator.id)));
    const kept = formData.selectedIntegrators
      .filter((integratorId) => offered.has(String(integratorId)))
      .slice(0, 1);
    if (kept.length !== formData.selectedIntegrators.length) {
      handleChange({ target: { name: 'selectedIntegrators', value: kept } });
    }
  }, [isScoreCustomer, integratorsLoaded, integrators, formData.selectedIntegrators, handleChange]);

  const handleIntegratorToggle = async (integratorId) => {
    const alreadySelected = selectedIntegrators.includes(integratorId);
    const nextIntegrators = alreadySelected
      ? selectedIntegrators.filter((selectedId) => selectedId !== integratorId)
      : isScoreCustomer
        ? [integratorId]
        : [...selectedIntegrators, integratorId];

    setSelectedIntegrators(nextIntegrators);
    setTouched((currentTouched) => ({
      ...currentTouched,
      selectedIntegrators: true,
    }));

    setFieldValue('selectedIntegrators', nextIntegrators);
    await validateField('selectedIntegrators', nextIntegrators);
  };

  const handleInitiativeToggle = async (initiativeId) => {
    const alreadySelected = selectedInitiatives.includes(initiativeId);
    // A DARE takes up one Initiative at sign-up; others may pick several.
    const nextInitiatives = alreadySelected
      ? selectedInitiatives.filter((selectedId) => selectedId !== initiativeId)
      : isDare ? [initiativeId] : [...selectedInitiatives, initiativeId];

    setTouched((currentTouched) => ({
      ...currentTouched,
      initiativeEcosystemToJoin: true,
    }));

    setFieldValue('initiativeEcosystemToJoin', nextInitiatives);
    await validateField('initiativeEcosystemToJoin', nextInitiatives);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setTouched({
      preferredEcosystemTypes: true,
      initiativeEcosystemToJoin: true,
      selectedIntegrators: true,
    });

    const isValid = await validateStep();
    if (isValid) {
      handleContinue();
    }
  };

  return (
    <Stack spacing={4} sx={{ maxWidth: 980, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={2} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Stack spacing={{ xs: 2.5, sm: 2 }}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                Ecosystem Preferences
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={authSubtextStyles}
              >
                Please choose your areas of interest. You may select multiple options.
              </Typography>
            </Stack>

            <Stack spacing={1}>
              {renderFieldLabel('Preferred Ecosystem Type(s)', true)}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(5, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {ecosystemTypeOptions.map(({ id, label, Icon }) => {
                  const isSelected = selectedTypes.includes(id);

                  return (
                    <ButtonBase
                      key={id}
                      onClick={() => handleTypeToggle(id)}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${
                          isSelected ? '#5a8f11' : '#E4E6F1'
                        }`,
                        backgroundColor: isSelected
                          ? 'rgba(90, 143, 17, 0.08)'
                          : '#ffffff',
                        minHeight: { xs: 112, sm: 132 },
                        px: 2,
                        py: { xs: 2, sm: 2.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        transition: 'all 180ms ease',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? 'rgba(90, 143, 17, 0.10)'
                            : 'rgba(28, 27, 31, 0.02)',
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 24,
                          color: isSelected ? 'primary.main' : 'text.primary',
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                        }}
                      >
                        {label}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
              {touched.preferredEcosystemTypes && errors.preferredEcosystemTypes ? (
                <Typography variant="caption" color="error.main">
                  {errors.preferredEcosystemTypes}
                </Typography>
              ) : null}
            </Stack>

            <Stack spacing={1}>
              {renderFieldLabel(
                isScoreCustomer ? 'Select your eK Score Integrator' : 'Select Ecosystem Integrators',
                !integratorsOptional
              )}
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 244,
                  borderRadius: 2,
                  borderColor: '#E4E6F1',
                  backgroundColor: '#ffffff',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 3 },
                }}
              >
                {filteredIntegrators.length === 0 ? (
                  <Box
                    sx={{
                      minHeight: 196,
                      display: 'grid',
                      placeItems: 'center',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 360, fontWeight: 600 }}
                    >
                      {integratorsOptional
                        ? 'No Ecosystem Integrator works in your country yet. You can continue: eKutir will review your DARE registration.'
                        : 'Ecosystem Integrators will show based on your Ecosystem Type preference.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    {selectedTypes.length > 0 && filteredIntegrators.map((integrator) => {
                      const isSelected = selectedIntegrators.includes(integrator.id);

                      return (
                        <ButtonBase
                          key={`integrator-${integrator.id}`}
                          onClick={() => handleIntegratorToggle(integrator.id)}
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            borderRadius: 3,
                            border: `1px solid ${
                              isSelected ? '#5a8f11' : '#E4E6F1'
                            }`,
                            backgroundColor: isSelected
                              ? 'rgba(90, 143, 17, 0.08)'
                              : '#ffffff',
                            px: 2.5,
                            py: 2.25,
                            alignItems: 'stretch',
                            justifyContent: 'stretch',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? 'rgba(90, 143, 17, 0.10)'
                                : 'rgba(28, 27, 31, 0.02)',
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ pt: 0.25 }}>
                              {isSelected ? (
                                <CheckCircleRounded color="primary" />
                              ) : (
                                <KeyboardArrowRightRounded sx={{ color: '#625b71' }} />
                              )}
                            </Box>
                            <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ color: 'text.primary', fontWeight: 700 }}
                              >
                                {integrator.integratorName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {integrator.integratorType || 'Ecosystem Integrator'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                )}
              </Paper>
              {touched.selectedEcosystems && errors.selectedEcosystems ? (
                <Typography variant="caption" color="error.main">
                  {errors.selectedEcosystems}
                </Typography>
              ) : null}
              {touched.selectedIntegrators && errors.selectedIntegrators ? (
                <Typography variant="caption" color="error.main">
                  {errors.selectedIntegrators}
                </Typography>
              ) : null}
            </Stack>

            <Stack spacing={1}>
              {renderFieldLabel(isDare ? 'Select an Initiative to take up (optional)' : 'Select Initiatives to Participate', !isDare)}
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 244,
                  borderRadius: 2,
                  borderColor: '#E4E6F1',
                  backgroundColor: '#ffffff',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 2, sm: 3 },
                }}
              >
                {initiatives.length === 0 ? (
                  <Box
                    sx={{
                      minHeight: 196,
                      display: 'grid',
                      placeItems: 'center',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 360, fontWeight: 600 }}
                    >
                      {!isDare
                        ? 'No initiatives are available for your location yet.'
                        : selectedIntegrators.length === 0
                          ? 'Select your area-based Ecosystem Integrator to see the Initiatives it offers.'
                          : 'Your Ecosystem Integrator has not joined an Initiative yet. You can continue and take one up later.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    {initiatives.map((initiative) => {
                      const isSelected = selectedInitiatives.includes(initiative.id);

                      return (
                        <ButtonBase
                          key={`initiative-${initiative.id}`}
                          onClick={() => handleInitiativeToggle(initiative.id)}
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            borderRadius: 3,
                            border: `1px solid ${
                              isSelected ? '#5a8f11' : '#E4E6F1'
                            }`,
                            backgroundColor: isSelected
                              ? 'rgba(90, 143, 17, 0.08)'
                              : '#ffffff',
                            px: 2.5,
                            py: 2.25,
                            alignItems: 'stretch',
                            justifyContent: 'stretch',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? 'rgba(90, 143, 17, 0.10)'
                                : 'rgba(28, 27, 31, 0.02)',
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ pt: 0.25 }}>
                              {isSelected ? (
                                <CheckCircleRounded color="primary" />
                              ) : (
                                <KeyboardArrowRightRounded sx={{ color: '#625b71' }} />
                              )}
                            </Box>
                            <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ color: 'text.primary', fontWeight: 700 }}
                              >
                                {initiative.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {initiative.description || 'Explore this initiative.'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                )}
              </Paper>
              {touched.initiativeEcosystemToJoin && errors.initiativeEcosystemToJoin ? (
                <Typography variant="caption" color="error.main">
                  {errors.initiativeEcosystemToJoin}
                </Typography>
              ) : null}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                pt: 2,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  order: { xs: 2, sm: 1 },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(90, 143, 17, 0.06)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Continue
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
}
